import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import { 
  Trophy,
  Users,
  Crown,
  User,
  Search,
  ShoppingBag,
  X,
  LogOut,
  Home,
  MessageSquare,
  Zap,
  BarChart3,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useBracketStore } from '@/stores/bracketStore'

export interface SidebarProps {
  className?: string
  onClose?: () => void
}

interface NavItemProps {
  href: string
  icon: React.ElementType
  label: string
  active?: boolean
  badge?: string
  onClick?: () => void
}

const NavItem: React.FC<NavItemProps> = ({
  href, icon: Icon, label, active, badge, onClick
}) => (
  <Link
    to={href}
    onClick={onClick}
    className={clsx(
      'group flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-colors duration-150',
      active 
        ? 'bg-white/[0.08] text-white'
        : 'text-white/50 hover:text-white/80 hover:bg-white/[0.04]'
    )}
  >
    <Icon className={clsx('h-4 w-4 flex-shrink-0', active ? 'text-primary-400' : 'text-white/40 group-hover:text-white/60')} />
    <span className="flex-1">{label}</span>
    {badge && (
      <span className={clsx(
        'text-[10px] font-semibold px-1.5 py-0.5 rounded-full',
        badge === 'Live' ? 'bg-emerald-500/20 text-emerald-400' : 
        badge === 'Pro' ? 'bg-primary-500/20 text-primary-400' :
        'bg-white/10 text-white/60'
      )}>
        {badge}
      </span>
    )}
  </Link>
)

const Sidebar: React.FC<SidebarProps> = ({ className, onClose }) => {
  const location = useLocation()
  const { user, logout, isAuthenticated } = useAuthStore()
  const activeBracketId = useBracketStore((s) => s.bracket?.id)

  const handleClick = () => onClose?.()
  const myBracketHref = activeBracketId ? `/bracket/${activeBracketId}` : '/bracket'
  const isMyBracketActive = location.pathname === '/bracket' || location.pathname.startsWith('/bracket/')

  return (
    <aside className={clsx(
      'flex flex-col h-full w-[240px] bg-[#0c0c1d] border-r border-white/[0.06]',
      className
    )}>
      {/* Logo */}
      <div className="flex items-center justify-between h-14 px-4 border-b border-white/[0.06]">
        <Link to="/" className="flex items-center gap-2.5" onClick={handleClick}>
          <div className="h-7 w-7 bg-gradient-to-br from-primary-500 to-primary-600 rounded-md flex items-center justify-center">
            <span className="text-white font-bold text-xs">B</span>
          </div>
          <span className="text-[15px] font-semibold text-white tracking-tight">
            BracketBlitz
          </span>
        </Link>
        {onClose && (
          <button onClick={onClose} className="p-1 rounded text-white/30 hover:text-white/60 lg:hidden">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
        <NavItem href="/" icon={Home} label="Home" active={location.pathname === '/'} onClick={handleClick} />

        {/* World Cup Blitz */}
        <div className="pt-2 pb-1 px-3">
          <span className="text-[10px] font-bold text-emerald-500/60 uppercase tracking-wider">⚽ World Cup 2026</span>
        </div>
        <NavItem href="/wc/knockout" icon={Trophy} label="Knockout" active={location.pathname === '/wc/knockout' || location.pathname === '/wc/wall'} badge="Live" onClick={handleClick} />
        <NavItem href="/wc/stats" icon={BarChart3} label="Stats" active={location.pathname === '/wc/stats'} onClick={handleClick} />
        <NavItem href="/wc/h2h" icon={Zap} label="H2H" active={location.pathname === '/wc/h2h'} onClick={handleClick} />

        {/* NCAA March Madness */}
        <div className="pt-3 pb-1 px-3">
          <span className="text-[10px] font-bold text-orange-500/60 uppercase tracking-wider">🏀 March Madness</span>
        </div>
        <NavItem href={myBracketHref} icon={Trophy} label="My Bracket" active={isMyBracketActive} badge="Live" onClick={handleClick} />
        <NavItem href="/leagues" icon={Users} label="Leagues" active={location.pathname.startsWith('/leagues')} onClick={handleClick} />
        <NavItem href="/leaderboard" icon={Crown} label="Leaderboard" active={location.pathname === '/leaderboard'} onClick={handleClick} />
        <NavItem href="/profile" icon={User} label="Profile" active={location.pathname === '/profile'} onClick={handleClick} />
        
        <div className="h-px bg-white/[0.06] my-3 mx-1" />
        
        <NavItem href="/community" icon={MessageSquare} label="Community" active={location.pathname.startsWith('/community')} onClick={handleClick} />
        <NavItem href="/scout" icon={Search} label="Scout" active={location.pathname === '/scout'} badge="Pro" onClick={handleClick} />
        <NavItem href="/store" icon={ShoppingBag} label="Store" active={location.pathname === '/store'} onClick={handleClick} />
      </nav>

      {/* Bottom section */}
      {isAuthenticated && user && (
        <div className="border-t border-white/[0.06]">
          {/* Coins */}
          <Link to="/store" onClick={handleClick} className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.04] transition-colors">
            <div className="h-8 w-8 rounded-md bg-amber-500/10 flex items-center justify-center">
              <Zap className="h-4 w-4 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white/40">Blitz Coins</p>
              <p className="text-sm font-semibold text-amber-400 tabular-nums">2,500</p>
            </div>
          </Link>

          {/* User */}
          <div className="flex items-center gap-3 px-4 py-3 border-t border-white/[0.06]">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-semibold text-white">
                {user.display_name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-white/80 truncate">
                {user.display_name || 'User'}
              </p>
              <p className="text-[11px] text-white/30 truncate">{user.email}</p>
            </div>
            <button 
              onClick={() => { logout(); handleClick(); }}
              className="p-1.5 rounded text-white/20 hover:text-white/50 hover:bg-white/[0.06] transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </aside>
  )
}

export { Sidebar }
